/* eslint-disable prettier/prettier */
import { FixedNumber } from '@ethersproject/bignumber'
import { loadingController } from '@/components/global-loading/global-loading-controller'
import { blockchainHandler } from '@/blockchain'
import moment, { Duration } from 'moment'
import Web3 from 'web3'
import { Zero } from '@/constants'
import { bnHelper } from './bignumber-helper'

const USD_DECIMALS = 18
const CHAIN_ID = process.env.VUE_APP_CHAIN_ID!
const SENCOND_PER_YEAR = FixedNumber.from(`${60 * 60 * 24 * 365}`)
const web3 = blockchainHandler.getWeb3(CHAIN_ID)!

async function sendRequest(fx, from, gasLimit = '') {
  return await new Promise((resolve, reject) => {
    fx.send({ from, gasLimit })
      .on('receipt', result => resolve(result))
      .on('error', error => reject(error))
  })
}

class PoolInfo {
  poolToken!: string
  allocPoint!: FixedNumber
  lastRewardTime!: string
  accRewardPerShare!: string
  amount!: FixedNumber
}

export class FarmLPHandler {
  routerContract: any
  farmContract: any
  tokenContract: any
  usdContract: any
  LPTokenContract: any
  rewardToken?: string

  lpPoolInfo?: PoolInfo
  tokenPerSecond = FixedNumber.from('0')
  totalAllocPoint = FixedNumber.from('0')
  lockDuration = moment.duration(0, 'second')

  farmAddress = ''
  farmAbiLink = require('./farm.lp.v2.abi.json')
  contractApproveAddress = process.env.VUE_APP_SB_ROUTER_ADDRESS!
  tokenAddress = ''
  lpAddress = ''
  tokenDecimals = 18
  apy = Zero

  farmType = 'sb'
  locktime = ''

  constructor(farmType, locktime) {
    this.farmType = farmType
    this.locktime = locktime
    if (farmType == 'SB') {
      this.tokenAddress = process.env.VUE_APP_SB_TOKEN_ADDRESS!
      this.lpAddress = process.env.VUE_APP_SB_TOKEN_LP_ADDRESS!
      this.tokenDecimals = 6

      switch (locktime) {
        case 7:
        case '7':
          this.farmAbiLink = require('./farm.lp.abi.json')
          this.contractApproveAddress = process.env.VUE_APP_SB_7D_FARM_LP_ADDRESS!
          this.farmAddress = process.env.VUE_APP_SB_7D_FARM_LP_ADDRESS!
          break
        case 30:
        case '30':
          this.farmAddress = process.env.VUE_APP_SB_30D_FARM_LP_ADDRESS!
          break
        case 60:
        case '60':
          this.farmAddress = process.env.VUE_APP_SB_60D_FARM_LP_ADDRESS!
          break
        case 90:
        case '90':
          this.farmAddress = process.env.VUE_APP_SB_90D_FARM_LP_ADDRESS!
          break
        case 180:
        case '180':
          this.farmAddress = process.env.VUE_APP_SB_180D_FARM_LP_ADDRESS!
          break
        case 360:
        case '360':
          this.farmAddress = process.env.VUE_APP_SB_360D_FARM_LP_ADDRESS!
          break
        default:
          this.farmAddress = process.env.VUE_APP_SB_360D_FARM_LP_ADDRESS!
          break
      }
    } else {
      this.tokenAddress = process.env.VUE_APP_STE_TOKEN_ADDRESS!
      this.lpAddress = process.env.VUE_APP_STE_TOKEN_LP_ADDRESS!
      switch (locktime) {
        case 7:
        case '7':
          this.farmAddress = process.env.VUE_APP_STE_7D_FARM_LP_ADDRESS!
          break
        case 30:
        case '30':
          this.farmAddress = process.env.VUE_APP_STE_30D_FARM_LP_ADDRESS!
          break
        case 60:
        case '60':
          this.farmAddress = process.env.VUE_APP_STE_60D_FARM_LP_ADDRESS!
          break
        case 90:
        case '90':
          this.farmAddress = process.env.VUE_APP_STE_90D_FARM_LP_ADDRESS!
          break
        case 180:
        case '180':
          this.farmAddress = process.env.VUE_APP_STE_180D_FARM_LP_ADDRESS!
          break
        case 360:
        case '360':
          this.farmAddress = process.env.VUE_APP_STE_360D_FARM_LP_ADDRESS!
          break
        default:
          this.farmAddress = process.env.VUE_APP_STE_360D_FARM_LP_ADDRESS!
          break
      }
    }

    this.tokenContract = new web3.eth.Contract(require('./erc20.abi.json'), this.tokenAddress)
    this.usdContract = new web3.eth.Contract(require('./erc20.abi.json'), process.env.VUE_APP_USD_ADDRESS)
    this.LPTokenContract = new web3.eth.Contract(require('./erc20.abi.json'), this.lpAddress)
    this.routerContract = new web3.eth.Contract(require('./sb.router.abi.json'), process.env.VUE_APP_SB_ROUTER_ADDRESS)
    this.farmContract = new web3.eth.Contract(this.farmAbiLink, this.farmAddress)
  }

  async load() {
    loadingController.increaseRequest()
    try {
      const methods = this.farmContract.methods
      const [
        rewardToken,
        tokenPerSecond,
        totalAllocPoint,
        tokenPoolInfor
      ] = await blockchainHandler.etherBatchRequest(web3, [
        methods.rewardToken(),
        methods.tokenPerSecond(),
        methods.totalAllocPoint(),
        methods.poolInfo(0)
      ])
      this.lpPoolInfo = {
        poolToken: (tokenPoolInfor as any).poolToken,
        allocPoint: FixedNumber.from(`${(tokenPoolInfor as any).allocPoint}`),
        lastRewardTime: moment(`${(tokenPoolInfor as any).lastRewardTime}`, 'X').toISOString(),
        amount: FixedNumber.from(`${bnHelper.fromDecimals((tokenPoolInfor as any).amount, this.tokenDecimals)}`)
      } as PoolInfo

      this.rewardToken = web3.utils.toChecksumAddress(`${rewardToken}`)
      this.tokenPerSecond = FixedNumber.from(`${bnHelper.fromDecimals(`${tokenPerSecond}`, this.tokenDecimals)}`)
      this.totalAllocPoint = FixedNumber.from(`${totalAllocPoint}`)

      this.tokenContract = new web3.eth.Contract(require('./erc20.abi.json'), this.rewardToken)
    } catch (error) {
      console.error(error)
    } finally {
      loadingController.decreaseRequest()
    }
  }

  injectMetamask(web3: Web3) {
    if (web3) {
      this.farmContract = new web3.eth.Contract(this.farmAbiLink, this.farmAddress)
      this.tokenContract = new web3.eth.Contract(require('./erc20.abi.json'), this.tokenAddress)
      this.usdContract = new web3.eth.Contract(require('./erc20.abi.json'), process.env.VUE_APP_USD_ADDRESS)
      this.LPTokenContract = new web3.eth.Contract(require('./erc20.abi.json'), this.lpAddress)
      this.routerContract = new web3.eth.Contract(
        require('./sb.router.abi.json'),
        process.env.VUE_APP_SB_ROUTER_ADDRESS
      )
    }
  }

  async addLpAndStake(
    _pid,
    _tokenA,
    _tokenB,
    _amountADesired,
    _amountBDesired,
    _amountAMin,
    _amountBMin,
    _locktime,
    account
  ) {
    const f = this.farmContract.methods.addLpAndStake(
      _pid,
      _tokenA,
      _tokenB,
      [
        bnHelper.toDecimalString(_amountADesired, this.tokenDecimals),
        bnHelper.toDecimalString(_amountBDesired, USD_DECIMALS),
        bnHelper.toDecimalString(_amountAMin, this.tokenDecimals),
        bnHelper.toDecimalString(_amountBMin, USD_DECIMALS)
      ],
      moment()
        .add(20, 'minutes')
        .unix(),
      _locktime
    )
    await sendRequest(f, account)
  }

  async stake(poolId, lpAmount, lockTime, account) {
    const f = this.farmContract.methods.stake(poolId, bnHelper.toDecimalString(lpAmount), lockTime)
    await sendRequest(f, account)
  }

  async lpApproved(account) {
    try {
      const allowance = await this.LPTokenContract.methods.allowance(account, this.contractApproveAddress).call()
      return !!+web3.utils.fromWei(allowance)
    } catch (error) {
      console.error(error)
      return false
    }
  }
  async lpApprove(account) {
    try {
      const f = this.LPTokenContract.methods.approve(this.contractApproveAddress, web3.utils.toWei(`${2 ** 64 - 1}`))
      await sendRequest(f, account)
    } catch (error) {
      console.error(error)
      throw error
    }
  }
  async tokenApproved(account) {
    try {
      const allowance = await this.tokenContract.methods.allowance(account, this.contractApproveAddress).call()
      return !!+web3.utils.fromWei(allowance)
    } catch (error) {
      console.error(error)
      return false
    }
  }
  async tokenApprove(account) {
    try {
      const f = this.tokenContract.methods.approve(this.contractApproveAddress, web3.utils.toWei(`${2 ** 64 - 1}`))
      await sendRequest(f, account)
    } catch (error) {
      console.error(error)
      throw error
    }
  }
  async usdApproved(account) {
    try {
      const allowance = await this.usdContract.methods.allowance(account, this.contractApproveAddress).call()
      return !!+web3.utils.fromWei(allowance)
    } catch (error) {
      console.error(error)
      return false
    }
  }
  async usdApprove(account) {
    try {
      const f = this.usdContract.methods.approve(this.contractApproveAddress, web3.utils.toWei(`${2 ** 64 - 1}`))
      await sendRequest(f, account)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async unstake(poolId, account) {
    const f = this.farmContract.methods.unstake(poolId)
    const res = await sendRequest(f, account)
    const unstakedEvent = (res as any).events['Unstaked']
    const amount = unstakedEvent.returnValues.amount as string

    return bnHelper.fromDecimals(amount)
  }

  async harvest(poolId, account) {
    const f = this.farmContract.methods.harvest(poolId)
    await sendRequest(f, account)
  }

  async setTokenPerSecond(tokenPerSecond, account) {
    const f = this.farmContract.methods.setTokenPerSecond(
      bnHelper.toDecimalString(tokenPerSecond, this.tokenDecimals),
      true
    )
    await sendRequest(f, account)
  }

  async getUserInfo(poolId, account) {
    const {
      0: amount,
      1: rewardDebt,
      2: rewardAmount,
      3: extraAmount,
      4: lastStakeTime,
      5: lockTime
    } = await this.farmContract.methods.userInfo(poolId, account).call()

    return {
      amount,
      rewardDebt,
      rewardAmount,
      extraAmount: bnHelper.fromDecimals(extraAmount),
      lastStakeTime: +lastStakeTime ? moment(+lastStakeTime * 1000) : null,
      lockTime: moment.duration(lockTime, 'second')
    }
  }

  async getRewardAmount(poolId, account) {
    const amount = await this.farmContract.methods.getRewardAmount(poolId, account).call()
    return FixedNumber.from(`${bnHelper.fromDecimals(amount, this.tokenDecimals)}`)
  }

  async getUserStakeBalance(poolId, account) {
    const amount = await this.farmContract.methods.getUserStakeBalance(poolId, account).call()
    return bnHelper.fromDecimals(amount)
  }

  totalLockedAmount = Zero
  async getTotalLockedAmount(poolId) {
    const { 4: amount } = await this.farmContract.methods.poolInfo(poolId).call()
    this.totalLockedAmount = bnHelper.fromDecimals(amount)
    return this.totalLockedAmount
  }
  userLength = 0
  async getUserLength(poolId) {
    const userLength = await this.farmContract.methods.getPoolUserLength(poolId).call()
    this.userLength = userLength
    return userLength
  }

  async getUserUSDBalance(account) {
    const allowance = await this.usdContract.methods.balanceOf(account).call()
    return FixedNumber.from(`${web3.utils.fromWei(allowance)}`)
  }
  async getUserLPBalance(account) {
    const allowance = await this.LPTokenContract.methods.balanceOf(account).call()
    return FixedNumber.from(`${web3.utils.fromWei(allowance)}`)
  }

  async getLPRatio() {
    let usdBalanceLP = Zero
    usdBalanceLP = await this.usdContract.methods
      .balanceOf(this.lpAddress)
      .call()
      .then(x => bnHelper.fromDecimals(x, USD_DECIMALS))

    const tokenBalanceLP = await this.tokenContract.methods
      .balanceOf(this.lpAddress)
      .call()
      .then(x => bnHelper.fromDecimals(x, this.tokenDecimals))

    const token2UsdPrice = usdBalanceLP.divUnsafe(tokenBalanceLP)
    return token2UsdPrice
  }

  async getTokenBalanceInContract() {
    const tokenBalanceInContract = await this.tokenContract.methods
      .balanceOf(this.farmAddress)
      .call()
      .then(x => bnHelper.fromDecimals(x, this.tokenDecimals))

    return tokenBalanceInContract
  }

  async getLPInfo() {
    const totalSupplyLP = await this.LPTokenContract.methods
      .totalSupply()
      .call()
      .then(x => bnHelper.fromDecimals(x))

    const tokenBalance = await this.tokenContract.methods
      .balanceOf(this.lpAddress)
      .call()
      .then(x => bnHelper.fromDecimals(x, this.tokenDecimals))

    const usdBalance = await this.usdContract.methods
      .balanceOf(this.lpAddress)
      .call()
      .then(x => bnHelper.fromDecimals(x, USD_DECIMALS))

    return { totalSupplyLP, tokenBalance, usdBalance }
  }

  async getTokenPerSecond() {
    const methods = this.farmContract.methods
    const [tokenPerSecond] = await blockchainHandler.etherBatchRequest(web3, [methods.tokenPerSecond()])
    this.tokenPerSecond = FixedNumber.from(`${bnHelper.fromDecimals(`${tokenPerSecond}`, this.tokenDecimals)}`)
  }

  async getFarmLiquidityAndApy(tokenPerSecond: any = null) {
    const usdBalanceLP = await this.usdContract.methods
      .balanceOf(this.lpAddress)
      .call()
      .then(x => bnHelper.fromDecimals(x))

    const tokenBalanceLP = await this.tokenContract.methods
      .balanceOf(this.lpAddress)
      .call()
      .then(x => bnHelper.fromDecimals(x, this.tokenDecimals))
    const totalSupplyLP: FixedNumber = await this.LPTokenContract.methods
      .totalSupply()
      .call()
      .then(x => bnHelper.fromDecimals(x))

    const poolInfo = await this.farmContract.methods.poolInfo(0).call()
    const farmLPBalance = FixedNumber.from(`${Web3.utils.fromWei(poolInfo[4])}`)

    const lp2UsdPrice = usdBalanceLP.mulUnsafe(FixedNumber.from('2')).divUnsafe(totalSupplyLP)
    const lpValue = usdBalanceLP.mulUnsafe(FixedNumber.from('2'))
    const poolLiquidityUsd = farmLPBalance.mulUnsafe(lp2UsdPrice)

    const token2UsdPrice = usdBalanceLP.divUnsafe(tokenBalanceLP)

    if (!tokenPerSecond) tokenPerSecond = this.tokenPerSecond

    const apy = poolLiquidityUsd.isZero()
      ? FixedNumber.from('0')
      : tokenPerSecond
          .mulUnsafe(SENCOND_PER_YEAR)
          .divUnsafe(this.totalAllocPoint)
          .mulUnsafe(this.lpPoolInfo!.allocPoint)
          .mulUnsafe(token2UsdPrice)
          .divUnsafe(poolLiquidityUsd)
          .mulUnsafe(FixedNumber.from('100'))

    this.apy = apy
    return { poolLiquidityUsd, apy, lp2UsdPrice, token2UsdPrice, farmLPBalance, lpValue }
  }

  async getPoolUserStakes(poolId) {
    const { 0: users, 1: amounts } = await this.farmContract.methods.getPoolUserStakes(poolId).call()

    const userStakes: any = []
    for (let index = 0; index < users.length; index++) {
      userStakes.push({
        address: users[index],
        amount: bnHelper.fromDecimals(amounts[index])
      })
    }
    return userStakes
  }
}
